// src/CorporateDashboard.js
import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';

import Navbar from './Components/Navbar/Navbar';
import Header from './Components/Header/BlueHeader';
import SinglePillNoCircle from './Components/SinglePillNoCircle'; // <--- no circle
import Footer from './Components/Footer/Footer';
import Sidebar from './Components/Sidebar/Sidebar';
import ARRGrowthChart from './Components/ARRGrowthChart/ARRGrowthChart';

import BuildingsTableCorporate from './Components/BuildingsTable/BuildingsTableCorporate';
import ContractsClosedTable from './Components/ContractsClosedTable/ContractsClosedTable';
import ContractTypeDistribution from './Components/ContractTypeDistribution';
import TopBuildingsByValueTable from './Components/TopBuildingsByValueTable';

// For icons
import { FaBuilding, FaMoneyBillWave, FaUserFriends, FaTools } from 'react-icons/fa';
import { Container, Row, Col, Spinner } from 'react-bootstrap';
import { FaRegEdit } from 'react-icons/fa';
import DashboardNavbar from './Components/Navbar/DashboardNavbar';

function CorporateDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem('managerToken');

  // Stats
  const [stats, setStats] = useState({
    totalARR: 0,
    activeContracts: 0,
    numberOfResidents: 0,
    postInspectionRepairs: 0,
    lifetimeValue: 0
  });

  // Building data
  const [buildingsData, setBuildingsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Searching / Filtering / Sorting / Pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Define tabs outside of loading conditions
  const corporateTabs = [
    { key: 'overview', label: 'Hudson', icon: <FaBuilding /> },
    { key: 'bmi', label: 'BMI', icon: <FaBuilding /> },
    { key: 'proposals', label: 'Proposals', icon: <FaRegEdit /> },
  ];

  // Sidebar handlers (move above isLoading check)
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const handleNavigate = (key) => {
    setActiveTab(key);
    if (key === 'overview') navigate('/corporate');
    if (key === 'proposals') navigate('/corporate/proposals');
    if (key === 'bmi') navigate('/bmi');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // (a) Stats
        const statsRes = await fetch(`${API_BASE_URL}/api/corporateStats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!statsRes.ok) throw new Error(`Stats fetch error: ${statsRes.status}`);
        const statsData = await statsRes.json();
        setStats({
          totalARR: statsData.totalARR || 0,
          activeContracts: statsData.activeContracts || 0,
          numberOfResidents: statsData.numberOfResidents || 0,
          postInspectionRepairs: statsData.postInspectionRepairs || 0,
          lifetimeValue: 0 // Will be calculated from buildings data
        });

        // 1) Create an array of names you want to exclude
        const EXCLUSIONS = [
          "123 Hudson St.",
          "456 Hudson Crescent",
          "789 Hudson Heights",
        ];
        
        // 2) Right after fetching building data:

        // (b) Buildings
        const bRes = await fetch(`${API_BASE_URL}/api/corporateBuildings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!bRes.ok) throw new Error(`Buildings fetch error: ${bRes.status}`);
        let bData = await bRes.json();
        if (!Array.isArray(bData)) bData = [];
        
        // 3) Filter out the excluded names
        const filtered = bData.filter((b) => !EXCLUSIONS.includes(b.name));
        
        // 4) Then setBuildingsData
        setBuildingsData(filtered);
        
        // 5) Calculate lifetime value from buildings data
        const totalLifetimeValue = filtered.reduce((sum, building) => {
          const lifetimeValue = building.lifetimeValue || 0;
          return sum + (typeof lifetimeValue === 'number' ? lifetimeValue : 0);
        }, 0);
        
        // Update stats with calculated lifetime value
        setStats(prevStats => ({
          ...prevStats,
          lifetimeValue: totalLifetimeValue
        }));
      } catch (err) {
        console.error('Corporate fetch error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Calculate total contracts (sum of all contracts arrays in buildingsData)
  const totalContracts = React.useMemo(() => {
    if (!buildingsData || buildingsData.length === 0) return 0;
    return buildingsData.reduce((sum, b) => sum + (Array.isArray(b.contracts) ? b.contracts.length : 0), 0);
  }, [buildingsData]);

  // Calculate MoM % Growth for ARR, Active Contracts, and Number of Buildings
  // 1. ARR MoM % - Use the same logic as ARRGrowthChart
  const arrMoM = (() => {
    const chartData = (() => {
      if (!buildingsData || buildingsData.length === 0) return [];
      const validBuildings = buildingsData.filter(b => b.contractValue && b.contractValue > 0 && b.createdAt);
      if (validBuildings.length === 0) return [];
      const sorted = validBuildings.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      const monthlyData = {};
      let cumulativeARR = 0;
      sorted.forEach(b => {
        const date = new Date(b.createdAt);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!monthlyData[monthKey]) monthlyData[monthKey] = { newARR: 0, cumulativeARR: 0, buildingCount: 0 };
        monthlyData[monthKey].newARR += b.contractValue;
        monthlyData[monthKey].buildingCount += 1;
      });
      Object.keys(monthlyData).sort().forEach(monthKey => {
        cumulativeARR += monthlyData[monthKey].newARR;
        monthlyData[monthKey].cumulativeARR = cumulativeARR;
      });
      return Object.entries(monthlyData)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => ({ ...value, month: key }));
    })();
    if (chartData.length < 2) return { value: '0.00%', color: '#888' };
    const prev = chartData[chartData.length - 2].cumulativeARR;
    const curr = chartData[chartData.length - 1].cumulativeARR;
    if (prev === 0) return { value: curr > 0 ? '+100.00%' : '0.00%', color: curr > 0 ? '#28a745' : '#888' };
    const percent = ((curr - prev) / prev) * 100;
    return {
      value: `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`,
      color: percent > 0 ? '#28a745' : percent < 0 ? '#dc3545' : '#888'
    };
  })();

  // 2. Active Contracts MoM % - Calculate cumulative contracts over time
  const contractsMoM = (() => {
    if (!buildingsData || buildingsData.length === 0) return { value: '0.00%', color: '#888' };
    
    // Group buildings by creation month and calculate cumulative contracts
    const validBuildings = buildingsData.filter(b => b.createdAt);
    const monthlyData = {};
    let cumulativeContracts = 0;
    
    validBuildings.forEach(b => {
      const date = new Date(b.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) monthlyData[monthKey] = { newContracts: 0, cumulativeContracts: 0 };
      const contractsCount = Array.isArray(b.contracts) ? b.contracts.length : 0;
      monthlyData[monthKey].newContracts += contractsCount;
    });
    
    // Calculate cumulative contracts
    Object.keys(monthlyData).sort().forEach(monthKey => {
      cumulativeContracts += monthlyData[monthKey].newContracts;
      monthlyData[monthKey].cumulativeContracts = cumulativeContracts;
    });
    
    const sortedData = Object.entries(monthlyData)
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => ({ ...value, month: key }));
    
    if (sortedData.length < 2) return { value: '0.00%', color: '#888' };
    const prev = sortedData[sortedData.length - 2].cumulativeContracts;
    const curr = sortedData[sortedData.length - 1].cumulativeContracts;
    if (prev === 0) return { value: curr > 0 ? '+100.00%' : '0.00%', color: curr > 0 ? '#28a745' : '#888' };
    const percent = ((curr - prev) / prev) * 100;
    return {
      value: `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`,
      color: percent > 0 ? '#28a745' : percent < 0 ? '#dc3545' : '#888'
    };
  })();

  // 3. Number of Buildings MoM % - Calculate cumulative buildings over time
  const buildingsMoM = (() => {
    if (!buildingsData || buildingsData.length === 0) return { value: '0.00%', color: '#888' };
    
    // Group buildings by creation month and calculate cumulative count
    const validBuildings = buildingsData.filter(b => b.createdAt);
    const monthlyData = {};
    let cumulativeBuildings = 0;
    
    validBuildings.forEach(b => {
      const date = new Date(b.createdAt);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[monthKey]) monthlyData[monthKey] = { newBuildings: 0, cumulativeBuildings: 0 };
      monthlyData[monthKey].newBuildings += 1;
    });
    
    // Calculate cumulative buildings
    Object.keys(monthlyData).sort().forEach(monthKey => {
      cumulativeBuildings += monthlyData[monthKey].newBuildings;
      monthlyData[monthKey].cumulativeBuildings = cumulativeBuildings;
    });
    
    const sortedData = Object.entries(monthlyData)
      .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
      .map(([key, value]) => ({ ...value, month: key }));
    
    if (sortedData.length < 2) return { value: '0.00%', color: '#888' };
    const prev = sortedData[sortedData.length - 2].cumulativeBuildings;
    const curr = sortedData[sortedData.length - 1].cumulativeBuildings;
    if (prev === 0) return { value: curr > 0 ? '+100.00%' : '0.00%', color: curr > 0 ? '#28a745' : '#888' };
    const percent = ((curr - prev) / prev) * 100;
    return {
      value: `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`,
      color: percent > 0 ? '#28a745' : percent < 0 ? '#dc3545' : '#888'
    };
  })();

  // If loading => show spinner
  if (isLoading) {
    return (
      <div style={{ display: 'flex' }}>
        <div className="dashboard-sidebar-host"
          onMouseEnter={() => setSidebarCollapsed(false)}
          onMouseLeave={() => setSidebarCollapsed(true)}
          style={{ height: '100vh' }}
        >
          <Sidebar
            activeKey={activeTab}
            onNavigate={handleNavigate}
            collapsed={sidebarCollapsed}
            onToggle={() => {}}
            tabs={corporateTabs}
          />
        </div>
        <div className="dashboard-content" style={{
          flex: 1,
          marginLeft: sidebarCollapsed ? '72px' : '240px',
          transition: 'margin-left 240ms',
        }}>
          <DashboardNavbar buildingName="Hudson Overview" contractLabel="Corporate Overview" />
          <Header isCorporate />
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Spinner animation="border" role="status" />
            <p>Loading Corporate Dashboard...</p>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  // 1) Filter data by searchTerm (match building name or manager)
  const filteredData = buildingsData.filter((b) => {
    const s = searchTerm.toLowerCase();
    const nameMatch = b.name?.toLowerCase().includes(s);
    const managerMatch = b.manager?.toLowerCase().includes(s);
    return nameMatch || managerMatch;
  });

  // 2) Sorting
  const sortedData = [...filteredData].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];

    // If sorting by "contractValue" or "resolvedJobs", parse as number
    if (['contractValue', 'resolvedJobs'].includes(sortColumn)) {
      valA = Number(valA) || 0;
      valB = Number(valB) || 0;
    }
    // If sorting by "renewalDate", parse as date
    if (sortColumn === 'renewalDate') {
      valA = valA ? Date.parse(valA) : 0;
      valB = valB ? Date.parse(valB) : 0;
    }

    // Compare strings or numbers
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // 3) Pagination
  const totalBuildings = sortedData.length;
  const totalPages = Math.ceil(totalBuildings / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = sortedData.slice(startIndex, endIndex);

  // Handlers
  function handleSort(columnKey) {
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  }
  function handlePrevPage() {
    if (currentPage > 1) setCurrentPage((prev) => prev - 1);
  }
  function handleNextPage() {
    if (currentPage < totalPages) setCurrentPage((prev) => prev + 1);
  }
  function handleRowsPerPageChange(e) {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }

  return (
    <div style={{ display: 'flex' }}>
      <div className="dashboard-sidebar-host"
        onMouseEnter={() => setSidebarCollapsed(false)}
        onMouseLeave={() => setSidebarCollapsed(true)}
        style={{ height: '100vh' }}
      >
        <Sidebar
          activeKey={activeTab}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={() => {}}
          tabs={corporateTabs}
        />
      </div>
      <div className="dashboard-content" style={{
        flex: 1,
        marginLeft: sidebarCollapsed ? '72px' : '240px',
        transition: 'margin-left 240ms',
      }}>
        <DashboardNavbar buildingName="Hudson Overview" contractLabel="Hudson Overview" />
        <Header isCorporate />

        {/* Pills with NO circle backgrounds */}
        <Container fluid style={{ width: '91.5%', margin: '20px auto' }}>
          <Row className="gx-3 gy-3">
            <Col xs={12} md={4}>
              <SinglePillNoCircle
                title="Total ARR"
                value={`$${stats.totalARR.toLocaleString()}`}
                Icon={FaMoneyBillWave}
                iconColor="#28a745"
                subLabel={
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#666', fontSize: '0.8rem', fontWeight: '500' }}>MoM % Growth:</div>
                    <div style={{ color: arrMoM.color, fontSize: '1rem', fontWeight: '600' }}>{arrMoM.value}</div>
                  </div>
                }
              />
            </Col>
            <Col xs={12} md={4}>
              <SinglePillNoCircle
                title="Active Contracts"
                value={totalContracts}
                Icon={FaBuilding}
                iconColor="#007bff"
                subLabel={
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#666', fontSize: '0.8rem', fontWeight: '500' }}>MoM % Growth:</div>
                    <div style={{ color: contractsMoM.color, fontSize: '1rem', fontWeight: '600' }}>{contractsMoM.value}</div>
                    <div style={{ color: '#666', fontSize: '0.7rem', fontWeight: '400', marginTop: '4px' }}>
                      {buildingsData.length} Buildings
                    </div>
                  </div>
                }
              />
            </Col>
            <Col xs={12} md={4}>
              <SinglePillNoCircle
                title="Lifetime Contract Value"
                value={`$${stats.lifetimeValue.toLocaleString()}`}
                Icon={FaMoneyBillWave}
                iconColor="#6f42c1"
                subLabel={
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#666', fontSize: '0.8rem', fontWeight: '500' }}>Total Value:</div>
                    <div style={{ color: '#6f42c1', fontSize: '1rem', fontWeight: '600' }}>All Time</div>
                  </div>
                }
              />
            </Col>
            {/* <Col xs={12} sm={6} md={3}>
              <SinglePillNoCircle
                title="Resident Lookups"
                value={stats.numberOfResidents}
                Icon={FaUserFriends}
                iconColor="#6f42c1"
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <SinglePillNoCircle
                title="New Jobs Resolved"
                value={stats.postInspectionRepairs}
                Icon={FaTools}
                iconColor="#e83e8c"
              />
            </Col> */}
          </Row>
        </Container>

        {/* ARR Growth Chart and Contracts Closed Table */}
        <Container fluid style={{ width: '91.5%', margin: '20px auto' }}>
          <Row className="gx-4">
            <Col xs={12} lg={6}>
              <ARRGrowthChart buildingsData={buildingsData} />
            </Col>
            <Col xs={12} lg={6}>
              <ContractsClosedTable buildingsData={buildingsData} />
            </Col>
          </Row>
          <Row className="gx-4 mt-4">
            <Col xs={12} lg={6}>
              <ContractTypeDistribution buildingsData={buildingsData} />
            </Col>
            <Col xs={12} lg={6}>
              <TopBuildingsByValueTable buildingsData={buildingsData} />
            </Col>
          </Row>
        </Container>

        {/* Inline Search Bar with thicker styling and no highlight */}
        <div
          style={{
            width: '90%',
            margin: '0 auto 1rem auto',
            display: 'flex',
            alignItems: 'center', 
          }}
        >
          {/* A relative container to hold the icon + input */}
          <div style={{ position: 'relative', width: '100%' }}>
            {/* Search Icon */}
            <i
              className="fa fa-search"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#999',
                fontSize: '1.2rem',
                pointerEvents: 'none', // let clicks pass through
              }}
            />

            {/* The Input */}
            <input
              type="text"
              placeholder="Search by Building or Manager..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                height: '56px',               // Thicker input
                fontSize: '1rem',
                paddingLeft: '2.5rem',        // Space for the icon
                borderRadius: '8px',
                border: 'none',
                outline: 'none',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)', // subtle shadow
              }}
            />
          </div>
        </div>

        {/* Table with the same District style but corporate columns */}
        <BuildingsTableCorporate
          pageData={pageData}
          sortedData={sortedData}
          startIndex={startIndex}
          endIndex={endIndex}
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          handleSort={handleSort}
          handlePrevPage={handlePrevPage}
          handleNextPage={handleNextPage}
          handleRowsPerPageChange={handleRowsPerPageChange}
        />


        <Footer />
      </div>
    </div>
  );
}

export default CorporateDashboard;
